/**
 * usePrefs — React hook wrapping prefs.ts.
 *
 * Returns the current prefs and a setter. The setter persists to
 * localStorage and broadcasts a `prochat-prefs-change` event so any
 * other component using the hook re-renders.
 */
import { useCallback, useEffect, useState } from 'react';
import { loadPrefs, savePref, emitChange, type Prefs } from '../lib/prefs.js';

export function usePrefs(): [Prefs, <K extends keyof Prefs>(k: K, v: Prefs[K]) => void] {
    const [prefs, setPrefs] = useState<Prefs>(() => loadPrefs());

    useEffect(() => {
        const onChange = () => setPrefs(loadPrefs());
        window.addEventListener('prochat-prefs-change', onChange);
        // Also reflect cross-tab edits.
        window.addEventListener('storage', onChange);
        return () => {
            window.removeEventListener('prochat-prefs-change', onChange);
            window.removeEventListener('storage', onChange);
        };
    }, []);

    const set = useCallback(<K extends keyof Prefs>(k: K, v: Prefs[K]) => {
        savePref(k, v);
        setPrefs(p => ({ ...p, [k]: v }));
        emitChange();
    }, []);

    return [prefs, set];
}
